import type { RxJsonSchema } from 'rxdb'

/**
 * Reminder Document Type (Denormalized for RxDB)
 */
export interface ReminderDocument {
  id: string
  envId: string
  taskId: string // Task this reminder belongs to
  reminderType: number // 0=Absolute, 1=Relative to due_date, 2=Relative to start_date, 3=Relative to created_on
  absoluteDate: number | null // Absolute date/time for reminder
  relativeOffsetMinutes: number | null // Offset in minutes (negative = before, positive = after)
  notifyUserId: string // User ID to notify
  notificationChannel: string // 'app', 'email', 'sms', 'push', 'webhook'
  isEnabled: boolean // Whether reminder is enabled
  isSent: boolean // Whether reminder has been sent
  sentAt: number | null // When reminder was sent
  recurrenceType: number | null // null=none, 0=daily, 1=weekly, 2=monthly, 3=yearly, 4=custom
  recurrenceInterval: number | null // Interval for recurrence (e.g., every 2 days)
  recurrenceDaysOfWeek: number | null // Bitmask for days of week
  recurrenceDayOfMonth: number | null // Day of month (1-31)
  recurrenceEndDate: number | null // End date for recurrence
  recurrenceMaxOccurrences: number | null // Max number of occurrences
  occurrenceCount: number // Number of times reminder has been sent
  lastSentAt: number | null // When reminder was last sent (for recurring)
  nextScheduledAt: number | null // Next scheduled time for reminder
  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Reminder
 */
export const reminderSchema: RxJsonSchema<ReminderDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    taskId: {
      type: 'string',
      maxLength: 36,
    },
    reminderType: {
      type: 'number',
      minimum: 0,
      maximum: 3,
      multipleOf: 1,
    },
    absoluteDate: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    relativeOffsetMinutes: {
      type: ['number', 'null'],
    },
    notifyUserId: {
      type: 'string',
      maxLength: 36,
    },
    notificationChannel: {
      type: 'string',
      maxLength: 20,
      default: 'app',
    },
    isEnabled: {
      type: 'boolean',
      default: true,
    },
    isSent: {
      type: 'boolean',
      default: false,
    },
    sentAt: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    recurrenceType: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 4,
      multipleOf: 1,
    },
    recurrenceInterval: {
      type: ['number', 'null'],
      minimum: 1,
    },
    recurrenceDaysOfWeek: {
      type: ['number', 'null'],
    },
    recurrenceDayOfMonth: {
      type: ['number', 'null'],
      minimum: 1,
      maximum: 31,
    },
    recurrenceEndDate: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    recurrenceMaxOccurrences: {
      type: ['number', 'null'],
      minimum: 1,
    },
    occurrenceCount: {
      type: 'number',
      minimum: 0,
      multipleOf: 1,
      default: 0,
    },
    lastSentAt: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    nextScheduledAt: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'taskId',
    'reminderType',
    'notifyUserId',
    'notificationChannel',
    'isEnabled',
    'isSent',
    'occurrenceCount',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'taskId',
    'updatedOn',
    ['taskId', 'isDeleted'],
    ['envId', 'isEnabled'],
  ],
}
